import React, { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
  Avatar,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { asyncStatus } from "../../../utils/asyncStatus";
import { themeOrange } from "../../../utils/colorTheme";
import { format } from "date-fns";
import { MdOutlineWork, MdArrowForward, MdAccessTime, MdLocationOn, MdEmail, MdPerson } from "react-icons/md";
import { FaCalendarAlt } from "react-icons/fa";
import { getServiceLocalImage } from "../../../utils/serviceImages";

const CurrentVendorJobsTab = () => {
  const navigate = useNavigate();
  const { get_job_by_vendor_status, get_job_by_vendor_data } = useSelector(
    (state) => state.serviceManage
  );

  const jobDoneLoader = get_job_by_vendor_status === asyncStatus.LOADING;
  const currentJobs = get_job_by_vendor_data?.filter((job) => job.status === "Assigned") || [];

  const parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return null;
    return format(d, "EEE, MMM d, yyyy");
  };

  const isUpcoming = (dateStr) => {
    const jobDate = parseDate(dateStr);
    if (!jobDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    jobDate.setHours(0, 0, 0, 0);
    return jobDate > today;
  };

  const isToday = (dateStr) => {
    const jobDate = parseDate(dateStr);
    if (!jobDate) return false;
    const today = new Date();
    return (
      jobDate.getDate() === today.getDate() &&
      jobDate.getMonth() === today.getMonth() &&
      jobDate.getFullYear() === today.getFullYear()
    );
  };

  if (jobDoneLoader) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }}>
        <CircularProgress size={30} sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  if (!currentJobs.length) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }} spacing={2}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MdOutlineWork size={36} color="#ccc" />
        </Box>
        <Typography sx={{ color: "#aaa", fontWeight: "500", fontSize: "16px" }}>
          No current jobs assigned
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      {/* Summary bar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography sx={{ fontWeight: "700", fontSize: "20px", color: "#1a1a1a" }}>
            {currentJobs.length}
          </Typography>
          <Typography sx={{ color: "#888", fontSize: "14px" }}>
            active {currentJobs.length === 1 ? "job" : "jobs"}
          </Typography>
        </Stack>
        <Chip
          label="Assigned"
          size="small"
          sx={{
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: "600",
            fontSize: "12px",
            border: "1px solid #a5d6a7",
          }}
        />
      </Stack>

      {/* Job Cards */}
      <Stack spacing={2}>
        {currentJobs.map((job) => {
          const customerName = `${job?.user?.first_name || ""} ${job?.user?.last_name || ""}`.trim() || "Customer";
          const initials = customerName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
          const serviceName = job?.service?.name || "Service";
          const price = job?.service?.price;
          const dateStr = job?.date_time_lock?.date;
          const todayJob = isToday(dateStr);
          const upcomingJob = isUpcoming(dateStr);

          return (
            <Box
              key={job.id}
              onClick={() => navigate(`/dashboard/job-done/${job?.id}`)}
              sx={{
                borderRadius: "14px",
                border: todayJob
                  ? `2px solid ${themeOrange}`
                  : "1.5px solid #ebebeb",
                backgroundColor: "white",
                overflow: "hidden",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: todayJob
                  ? `0 4px 20px ${themeOrange}25`
                  : "0 2px 8px rgba(0,0,0,0.05)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 24px rgba(0,0,0,0.1)`,
                  borderColor: themeOrange,
                },
              }}
            >
              {/* Today banner */}
              {todayJob && (
                <Box
                  sx={{
                    px: 3,
                    py: 0.75,
                    background: `linear-gradient(90deg, ${themeOrange} 0%, #c53f10 100%)`,
                  }}
                >
                  <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "700", letterSpacing: "0.5px" }}>
                    TODAY'S JOB
                  </Typography>
                </Box>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} sx={{ p: 2.5 }} gap={2}>
                {/* Service image */}
                <Box
                  sx={{
                    width: { xs: "100%", sm: 110 },
                    height: { xs: 140, sm: 110 },
                    borderRadius: "10px",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={getServiceLocalImage(serviceName)}
                    alt={serviceName}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>

                {/* Main content */}
                <Stack flex={1} spacing={1.5}>
                  {/* Top row: service name + price */}
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography
                      sx={{
                        fontWeight: "700",
                        fontSize: "17px",
                        color: "#1a1a1a",
                        lineHeight: 1.3,
                      }}
                    >
                      {serviceName}
                    </Typography>
                    {price && (
                      <Typography
                        sx={{
                          fontWeight: "800",
                          fontSize: "18px",
                          color: themeOrange,
                          flexShrink: 0,
                        }}
                      >
                        ${(+price).toLocaleString()}
                      </Typography>
                    )}
                  </Stack>

                  {/* Info rows */}
                  <Stack spacing={0.75}>
                    {/* Customer */}
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: "11px",
                          fontWeight: "700",
                          backgroundColor: "#071d5b",
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </Avatar>
                      <Typography sx={{ fontSize: "14px", color: "#444", fontWeight: "500" }}>
                        {customerName}
                      </Typography>
                    </Stack>

                    {/* Email */}
                    {job?.user?.email && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdEmail size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888" }}>
                          {job.user.email}
                        </Typography>
                      </Stack>
                    )}

                    {/* Date */}
                    {dateStr && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <FaCalendarAlt size={14} color="#999" style={{ flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: todayJob ? themeOrange : "#666",
                            fontWeight: todayJob ? "600" : "400",
                          }}
                        >
                          {formatDate(dateStr)}
                        </Typography>
                        {todayJob && (
                          <Chip
                            label="Today"
                            size="small"
                            sx={{
                              height: "18px",
                              fontSize: "10px",
                              fontWeight: "700",
                              backgroundColor: `${themeOrange}15`,
                              color: themeOrange,
                              border: `1px solid ${themeOrange}40`,
                            }}
                          />
                        )}
                        {upcomingJob && !todayJob && (
                          <Chip
                            label="Upcoming"
                            size="small"
                            sx={{
                              height: "18px",
                              fontSize: "10px",
                              fontWeight: "600",
                              backgroundColor: "#e3f2fd",
                              color: "#1976d2",
                              border: "1px solid #90caf9",
                            }}
                          />
                        )}
                      </Stack>
                    )}

                    {/* Time/Shift */}
                    {job?.date_time_lock?.time && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdAccessTime size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888" }}>
                          {job.date_time_lock.time}
                        </Typography>
                      </Stack>
                    )}

                    {/* Location */}
                    {job?.location && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdLocationOn size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#888",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "300px",
                          }}
                        >
                          {job.location}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>

                {/* Right: status + arrow */}
                <Stack
                  alignItems={{ xs: "flex-start", sm: "flex-end" }}
                  justifyContent="space-between"
                  flexShrink={0}
                  gap={1}
                >
                  <Chip
                    label="Assigned"
                    size="small"
                    sx={{
                      backgroundColor: "#e8f5e9",
                      color: "#2e7d32",
                      fontWeight: "700",
                      fontSize: "12px",
                      border: "1px solid #a5d6a7",
                    }}
                  />
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: `${themeOrange}10`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MdArrowForward size={18} color={themeOrange} />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default CurrentVendorJobsTab;
